import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  Signal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ProjectGridComponent } from '@app/components/project-grid/project-grid.component';
import type { Contact } from '@app/models/account.model';
import type { Project } from '@app/models/project.models';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ProjectsStore } from 'src/app/+store/projects/projects.store';

type SortKey = 'latest' | 'oldest' | 'name_az' | 'name_za';

interface StatusItemDef {
  id: number;
  key: string;
}

@Component({
  selector: 'app-projects-tab',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ProjectGridComponent, ButtonComponent],
  templateUrl: './projects-tab.component.html',
  styleUrl: './projects-tab.component.scss',
})
export class ProjectsTabComponent implements OnInit {
  @Input() contact?: Signal<Contact | null>;

  private readonly translate = inject(TranslateService);
  protected readonly projectsStore = inject(ProjectsStore);

  // filter state
  protected draftStatuses = signal<Set<number>>(new Set());
  protected appliedStatuses = signal<Set<number>>(new Set());
  protected searchTerm = signal<string>('');
  protected sortOrder = signal<SortKey>('latest');

  // pagination (page size only; no paging UI as requested)
  protected pageSize = signal<number>(20);
  protected currentPage = signal<number>(1);

  // status items (keys are translated in template)
  private readonly STATUS_DEFS: StatusItemDef[] = [
    { id: 0, key: 'admin.contacts.projects.status.collecting' },
    { id: 1, key: 'admin.contacts.projects.status.preparing' },
    { id: 2, key: 'admin.contacts.projects.status.verifying' },
    { id: 3, key: 'admin.contacts.projects.status.awaitingPublish' },
    { id: 4, key: 'admin.contacts.projects.status.closed' },
  ];
  protected statusItems = computed(() => this.STATUS_DEFS);

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => {});
  }

  // helper
  private getStateId(project: Project): number {
    const raw = (project as unknown as { state?: unknown })?.state;
    const number = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
    return Number.isFinite(number) ? number : -1;
  }

  // loading state
  protected isLoading = computed<boolean>(() => {
    const ongoing = this.projectsStore.ongoingProjectEntities()?.length ?? 0;
    const prepared = this.projectsStore.preparedProjectEntities()?.length ?? 0;
    const closed = this.projectsStore.closedProjectEntities()?.length ?? 0;
    const total = ongoing + prepared + closed;
    return total === 0;
  });

  // unify all projects (dedupe by id)
  private allProjects = computed<Project[]>(() => {
    const map = new Map<string, Project>();
    for (const project of [
      ...(this.projectsStore.ongoingProjectEntities() ?? []),
      ...(this.projectsStore.preparedProjectEntities() ?? []),
      ...(this.projectsStore.closedProjectEntities() ?? []),
    ]) {
      map.set(project.id, project);
    }
    return Array.from(map.values());
  });

  // filtered + sorted + limited (pageSize only)
  protected visibleProjects = computed<Project[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const applied = this.appliedStatuses();
    const order = this.sortOrder();
    const limit = this.pageSize();

    let list = this.allProjects();

    if (applied.size) {
      list = list.filter((project) => applied.has(this.getStateId(project)));
    }

    if (term) {
      list = list.filter((project) =>
        [project.name, project.city, project.country]
          .map((v) => (v ?? '').toString().toLowerCase())
          .some((v) => v.includes(term))
      );
    }

    const byName = (project: Project) =>
      (project.name ?? '').toString().toLowerCase();
    const byDate = (project: Project) => {
      const ts = Date.parse(
        (project as any).targetColletionEndDate ??
          (project as any).createdAt ??
          ''
      );
      return Number.isNaN(ts) ? 0 : ts;
    };

    list = list.slice().sort((a, b) => {
      switch (order) {
        case 'name_az':
          return byName(a) < byName(b) ? -1 : byName(a) > byName(b) ? 1 : 0;
        case 'name_za':
          return byName(a) > byName(b) ? -1 : byName(a) < byName(b) ? 1 : 0;
        case 'oldest':
          return byDate(a) - byDate(b);
        case 'latest':
        default:
          return byDate(b) - byDate(a);
      }
    });

    return list.slice(0, limit);
  });

  protected totalAfterFilters = computed<number>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const applied = this.appliedStatuses();

    let list = this.allProjects();

    if (applied.size) {
      list = list.filter((project) => applied.has(this.getStateId(project)));
    }

    if (term) {
      list = list.filter((project) =>
        [project.name, project.city, project.country]
          .map((v) => (v ?? '').toString().toLowerCase())
          .some((v) => v.includes(term))
      );
    }

    return list.length;
  });

  // UI handlers
  onStatusCheckboxChange(statusId: number, checked: boolean): void {
    const next = new Set(this.draftStatuses());
    checked ? next.add(statusId) : next.delete(statusId);
    this.draftStatuses.set(next);
  }

  onApplyFiltersClick(): void {
    this.appliedStatuses.set(new Set(this.draftStatuses()));
  }

  onClearFiltersClick(): void {
    this.draftStatuses.set(new Set());
    this.appliedStatuses.set(new Set());
    this.searchTerm.set('');
  }

  onClearStatusOnly(): void {
    this.draftStatuses.set(new Set());
    this.appliedStatuses.set(new Set());
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.searchTerm.set(value);
  }

  onPageSizeSelect(event: Event): void {
    const value = Number((event.target as HTMLSelectElement)?.value ?? 20);
    if (!Number.isNaN(value) && value > 0) this.pageSize.set(value);
  }

  onSortSelect(event: Event): void {
    const value = (event.target as HTMLSelectElement)?.value as SortKey;
    if (
      value === 'latest' ||
      value === 'oldest' ||
      value === 'name_az' ||
      value === 'name_za'
    ) {
      this.sortOrder.set(value);
    }
  }

  isDraftChecked(statusId: number): boolean {
    return this.draftStatuses().has(statusId);
  }
}
